// Browser-compatible Sync Manager for ACCTOO
// Handles two-way sync between SQLite and Firebase

import BrowserSQLiteManager from './BrowserSQLiteManager.js';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc
} from 'firebase/firestore';

class BrowserSyncManager {
  constructor(firebaseDb, sqliteManager) {
    this.firebaseDb = firebaseDb;
    this.sqliteManager = sqliteManager;
    this.syncInProgress = false;
    this.lastSyncTime = null;
  }

  // =============================================
  // MAIN SYNC OPERATIONS
  // =============================================

  // Full sync (both directions)
  async fullSync() {
    if (this.syncInProgress) {
      console.log('⚠️ Sync already in progress, skipping...');
      return { success: false, message: 'Sync already in progress' };
    }

    try {
      this.syncInProgress = true;
      console.log('🔄 Starting full sync...');

      // Step 1: Sync from Firebase to SQLite
      await this.syncFromFirebase();

      // Step 2: Sync from SQLite to Firebase
      await this.syncToFirebase();

      // Step 3: Resolve conflicts
      await this.resolveConflicts();

      this.lastSyncTime = new Date();
      console.log('✅ Full sync completed successfully');
      
      return { success: true, message: 'Sync completed successfully' };
    } catch (error) {
      console.error('❌ Full sync failed:', error);
      return { success: false, error: error.message };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Sync from Firebase to SQLite
  async syncFromFirebase() {
    try {
      console.log('📥 Syncing from Firebase to SQLite...');

      const collections = [
        'parties', 'items', 'sales_bills', 'challan', 
        'quotation', 'purchase_bills', 'purchase_orders', 'payments', 'expenses'
      ];

      for (const collectionName of collections) {
        await this.syncCollectionFromFirebase(collectionName);
      }

      console.log('✅ Firebase to SQLite sync completed');
    } catch (error) {
      console.error('❌ Firebase to SQLite sync failed:', error);
      throw error;
    }
  }

  // Sync from SQLite to Firebase
  async syncToFirebase() {
    try {
      console.log('📤 Syncing from SQLite to Firebase...');

      // Get pending sync records
      const pendingRecords = await this.sqliteManager.getPendingSyncRecords();

      for (const record of pendingRecords) {
        await this.syncRecordToFirebase(record);
      }

      console.log('✅ SQLite to Firebase sync completed');
    } catch (error) {
      console.error('❌ SQLite to Firebase sync failed:', error);
      throw error;
    }
  }

  // =============================================
  // COLLECTION SYNC METHODS
  // =============================================

  // Sync specific collection from Firebase
  async syncCollectionFromFirebase(collectionName) {
    try {
      const companyId = this.sqliteManager.companyId;
      const firebaseCollection = collection(this.firebaseDb, 'companies', companyId, collectionName);
      
      // Get all documents from Firebase
      const snapshot = await getDocs(firebaseCollection);
      
      for (const docSnapshot of snapshot.docs) {
        const firebaseData = docSnapshot.data();
        const recordId = docSnapshot.id;

        // Check if record exists in SQLite
        const existingRecords = await this.sqliteManager.select(collectionName, { id: recordId });
        
        if (existingRecords.length === 0) {
          // Insert new record
          await this.sqliteManager.insert(collectionName, {
            ...firebaseData,
            sync_status: 'synced'
          });
          console.log(`✅ Inserted new record: ${collectionName}/${recordId}`);
        } else {
          // Update existing record if Firebase is newer
          const localRecord = existingRecords[0];
          if (this.isFirebaseNewer(firebaseData, localRecord)) {
            await this.sqliteManager.update(collectionName, recordId, {
              ...firebaseData,
              sync_status: 'synced'
            });
            console.log(`✅ Updated record: ${collectionName}/${recordId}`);
          }
        }
      }

      console.log(`✅ Synced collection: ${collectionName}`);
    } catch (error) {
      console.error(`❌ Sync collection failed: ${collectionName}`, error);
      throw error;
    }
  }

  // Sync specific record to Firebase
  async syncRecordToFirebase(record) {
    try {
      const { table_name, record_id, operation, data } = record;
      const companyId = this.sqliteManager.companyId;
      const firebaseDoc = doc(this.firebaseDb, 'companies', companyId, table_name, record_id);
      
      const recordData = JSON.parse(data);

      switch (operation) {
        case 'create':
          await setDoc(firebaseDoc, recordData);
          break;
        case 'update':
          await updateDoc(firebaseDoc, recordData);
          break;
        case 'delete':
          await deleteDoc(firebaseDoc);
          break;
        default:
          console.warn(`⚠️ Unknown operation: ${operation}`);
          return;
      }

      // Mark as synced
      await this.markSyncRecordSynced(record.id);
      console.log(`✅ Synced record to Firebase: ${table_name}/${record_id}`);
    } catch (error) {
      console.error(`❌ Sync record to Firebase failed:`, error);
      throw error;
    }
  }

  // Mark sync record as synced
  async markSyncRecordSynced(recordId) {
    try {
      await this.sqliteManager.update('sync_queue', recordId, {
        status: 'synced',
        synced_at: new Date().toISOString()
      });

      console.log('✅ Sync record marked as synced:', recordId);
    } catch (error) {
      console.error('❌ Mark sync record synced failed:', error);
      throw error;
    }
  }

  // =============================================
  // CONFLICT RESOLUTION
  // =============================================

  // Resolve conflicts between local and remote data
  async resolveConflicts() {
    try {
      console.log('🔍 Resolving conflicts...');

      const collections = [
        'parties', 'items', 'sales_bills', 'challan', 
        'quotation', 'purchase_bills', 'purchase_orders', 'payments', 'expenses'
      ];

      for (const collectionName of collections) {
        await this.resolveCollectionConflicts(collectionName);
      }

      console.log('✅ Conflict resolution completed');
    } catch (error) {
      console.error('❌ Conflict resolution failed:', error);
      throw error;
    }
  }

  // Resolve conflicts for specific collection
  async resolveCollectionConflicts(collectionName) {
    try {
      const companyId = this.sqliteManager.companyId;
      const localRecords = await this.sqliteManager.select(collectionName, { company_id: companyId });
      
      for (const localRecord of localRecords) {
        if (localRecord.sync_status === 'pending') {
          // Check if record exists in Firebase
          const firebaseDoc = doc(this.firebaseDb, 'companies', companyId, collectionName, localRecord.id);
          const firebaseSnapshot = await getDoc(firebaseDoc);
          
          if (firebaseSnapshot.exists()) {
            const firebaseData = firebaseSnapshot.data();
            
            // Check for conflicts
            if (this.hasConflict(localRecord, firebaseData)) {
              await this.resolveRecordConflict(collectionName, localRecord, firebaseData);
            }
          }
        }
      }
    } catch (error) {
      console.error(`❌ Resolve collection conflicts failed: ${collectionName}`, error);
      throw error;
    }
  }

  // Resolve conflict for specific record
  async resolveRecordConflict(collectionName, localRecord, firebaseRecord) {
    try {
      // Use timestamp-based resolution
      const localTime = new Date(localRecord.updated_at).getTime();
      const firebaseTime = new Date(firebaseRecord.updated_at).getTime();
      
      if (localTime > firebaseTime) {
        // Local is newer, update Firebase
        const firebaseDoc = doc(this.firebaseDb, 'companies', this.sqliteManager.companyId, collectionName, localRecord.id);
        await updateDoc(firebaseDoc, localRecord);
        console.log(`✅ Resolved conflict: Local wins for ${collectionName}/${localRecord.id}`);
      } else {
        // Firebase is newer, update local
        await this.sqliteManager.update(collectionName, localRecord.id, {
          ...firebaseRecord,
          sync_status: 'synced'
        });
        console.log(`✅ Resolved conflict: Firebase wins for ${collectionName}/${localRecord.id}`);
      }
    } catch (error) {
      console.error(`❌ Resolve record conflict failed:`, error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if Firebase data is newer than local
  isFirebaseNewer(firebaseData, localData) {
    const firebaseTime = new Date(firebaseData.updated_at || firebaseData.created_at).getTime();
    const localTime = new Date(localData.updated_at || localData.created_at).getTime();
    return firebaseTime > localTime;
  }

  // Check if there's a conflict between local and remote data
  hasConflict(localRecord, firebaseRecord) {
    // Simple conflict detection based on updated_at timestamps
    const localTime = new Date(localRecord.updated_at).getTime();
    const firebaseTime = new Date(firebaseRecord.updated_at).getTime();
    
    // Consider it a conflict if both were updated within 5 minutes of each other
    const timeDiff = Math.abs(localTime - firebaseTime);
    return timeDiff < 5 * 60 * 1000; // 5 minutes
  }

  // Get sync status
  async getSyncStatus() {
    try {
      const pendingRecords = await this.sqliteManager.getPendingSyncRecords();
      const stats = await this.sqliteManager.getDatabaseStats();
      
      return {
        lastSyncTime: this.lastSyncTime,
        pendingRecords: pendingRecords.length,
        totalRecords: Object.values(stats).reduce((sum, count) => sum + count, 0),
        syncInProgress: this.syncInProgress
      };
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }

  // Force sync specific record
  async forceSyncRecord(tableName, recordId) {
    try {
      const records = await this.sqliteManager.select(tableName, { id: recordId });
      
      if (records.length === 0) {
        throw new Error('Record not found');
      }

      const recordData = records[0];
      const companyId = this.sqliteManager.companyId;
      const firebaseDoc = doc(this.firebaseDb, 'companies', companyId, tableName, recordId);
      
      await setDoc(firebaseDoc, recordData);
      await this.sqliteManager.update(tableName, recordId, { sync_status: 'synced' });
      
      console.log(`✅ Force synced record: ${tableName}/${recordId}`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Force sync record failed:`, error);
      throw error;
    }
  }

  // Clear sync queue
  async clearSyncQueue() {
    try {
      const records = await this.sqliteManager.select('sync_queue', {
        company_id: this.sqliteManager.companyId,
        device_id: this.sqliteManager.deviceId
      });
      
      for (const record of records) {
        await this.sqliteManager.delete('sync_queue', record.id);
      }
      
      console.log('✅ Sync queue cleared');
      return { success: true };
    } catch (error) {
      console.error('❌ Clear sync queue failed:', error);
      throw error;
    }
  }

  // Get sync history
  async getSyncHistory(limit = 100) {
    try {
      const records = await this.sqliteManager.select('sync_queue', {
        company_id: this.sqliteManager.companyId,
        device_id: this.sqliteManager.deviceId
      });
      
      return records.slice(0, limit);
    } catch (error) {
      console.error('❌ Get sync history failed:', error);
      throw error;
    }
  }
}

export default BrowserSyncManager;
